import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { useState, useEffect } from 'react';
import { apiRawFetch } from '../../main/client';

interface PropertyInviteUserDialogProps {
    open: boolean;
    propertyId: number;
    propertyName: string;
    onClose: () => void;
    onSent: () => void;
}

const PropertyInviteUserDialog = ({
    open,
    propertyId,
    propertyName,
    onClose,
    onSent,
}: PropertyInviteUserDialogProps) => {
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (open) {
            setEmail('');
            setName('');
            setError(null);
        }
    }, [open]);

    const handleClose = () => {
        if (!loading) {
            onClose();
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setError('Please enter a valid email address');
            return;
        }

        if (!name.trim()) {
            setError('Please enter a name');
            return;
        }

        try {
            setLoading(true);
            const response = await apiRawFetch('api/invitations', 'POST', {
                email,
                name,
                permissions: [{ type: 'propertyAdmin', propertyId }],
            });

            if (!response.ok) {
                const errorData = (await response.json().catch(() => ({}))) as {
                    error?: string;
                };
                throw new Error(
                    errorData.error ??
                        'An invitation for this email already exists or user already registered'
                );
            }

            onSent();
        } catch (err) {
            setError(
                err instanceof Error ? err.message : 'Failed to send invitation'
            );
        } finally {
            setLoading(false);
        }
    };

    const textFieldSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: 0,
            '& input': {
                backgroundColor: '#0f0f23',
            },
            '& fieldset': {
                borderWidth: '2px',
                borderColor: '#374151',
            },
            '&:hover fieldset': {
                borderWidth: '2px',
                borderColor: '#6366f1',
            },
            '&.Mui-focused fieldset': {
                borderWidth: '3px',
                borderColor: '#6366f1',
            },
        },
        '& .MuiInputLabel-root': {
            fontWeight: 700,
            textTransform: 'uppercase',
            fontSize: '1rem',
            letterSpacing: '0.05em',
        },
    };

    return (
        <Dialog
            open={open}
            onClose={(_event, reason) => {
                // Prevent backdrop click from closing - handled by slotProps.backdrop
                if (reason === 'backdropClick') {
                    return;
                }
                handleClose();
            }}
            maxWidth="sm"
            fullWidth
            disableRestoreFocus
            disableEnforceFocus
            slotProps={{
                backdrop: {
                    onClick: e => e.stopPropagation(),
                },
                paper: {
                    sx: {
                        borderRadius: 0,
                        border: '4px solid #6366f1',
                        backgroundColor: '#1a1a2e',
                    },
                },
            }}
        >
            <form onSubmit={handleSubmit}>
                <DialogTitle
                    sx={{
                        borderBottom: '3px solid #6366f1',
                        backgroundColor: '#0f0f23',
                        fontWeight: 900,
                        fontSize: '1.5rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        py: 2.5,
                    }}
                >
                    Invite User to {propertyName}
                </DialogTitle>
                <DialogContent
                    sx={{
                        backgroundColor: '#1a1a2e',
                        p: 0,
                    }}
                >
                    <Stack sx={{ py: 3, px: 3 }} spacing={2.5}>
                        {error && (
                            <Alert
                                severity="error"
                                onClose={() => setError(null)}
                                sx={{
                                    borderRadius: 0,
                                    border: '3px solid #ef4444',
                                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                                    fontWeight: 600,
                                    '& .MuiAlert-icon': {
                                        color: '#ef4444',
                                    },
                                }}
                            >
                                {error}
                            </Alert>
                        )}
                        <TextField
                            autoFocus
                            label="Email Address"
                            type="email"
                            fullWidth
                            variant="outlined"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            required
                            disabled={loading}
                            sx={textFieldSx}
                        />
                        <TextField
                            label="Full Name"
                            type="text"
                            fullWidth
                            variant="outlined"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            required
                            disabled={loading}
                            sx={textFieldSx}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions
                    sx={{
                        borderTop: '3px solid #374151',
                        backgroundColor: '#0f0f23',
                        p: 3,
                        gap: 1.5,
                        justifyContent: 'flex-end',
                    }}
                >
                    <Button
                        onClick={handleClose}
                        disabled={loading}
                        variant="outlined"
                        sx={{
                            borderRadius: 0,
                            borderWidth: '2px',
                            borderColor: '#374151',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            px: 3,
                            py: 1,
                            '&:hover': {
                                borderWidth: '2px',
                                borderColor: '#374151',
                                backgroundColor: 'rgba(55, 65, 81, 0.1)',
                            },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={loading}
                        sx={{
                            borderRadius: 0,
                            border: '2px solid #6366f1',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            px: 3,
                            py: 1,
                        }}
                    >
                        {loading ? 'Sending...' : 'Send Invitation'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default PropertyInviteUserDialog;
